// src/pages/Orders.jsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../utils/api";

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    const res = await api.getMyOrders();
    if (res.success) {
      setOrders(res.data.orders);
    }
    setLoading(false);
  };

  const handleCancelOrder = async (orderId) => {
    if (!confirm("Are you sure you want to cancel this order?")) return;

    const res = await api.cancelOrder(orderId);
    if (res.success) {
      alert("Order cancelled successfully");
      fetchOrders(); // Refresh orders
    } else {
      alert(res.message || "Failed to cancel order");
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      Pending: "bg-yellow-100 text-yellow-800",
      Processing: "bg-blue-100 text-blue-800",
      Shipped: "bg-purple-100 text-purple-800",
      Delivered: "bg-green-100 text-green-800",
      Cancelled: "bg-red-100 text-red-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center">Loading orders...</div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16 text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">No Orders Yet</h2>
        <p className="text-gray-600 mb-8">Start shopping to see your orders here!</p>
        <button
          onClick={() => navigate("/products")}
          className="bg-purple-600 text-white px-8 py-3 rounded-lg hover:bg-purple-700 transition"
        >
          Browse Products
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">My Orders</h1>

      <div className="space-y-6">
        {orders.map((order) => (
          <div key={order._id} className="bg-white rounded-lg shadow-md p-6">
            {/* Order Header */}
            <div className="flex justify-between items-start mb-4 pb-4 border-b">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  Order #{order.orderNumber}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Placed on {new Date(order.createdAt).toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </p>
              </div>
              <div className="text-right">
                <span
                  className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(
                    order.orderStatus
                  )}`}
                >
                  {order.orderStatus}
                </span>
                <p className="text-lg font-bold text-purple-600 mt-2">
                  ₹{order.totalAmount.toLocaleString()}
                </p>
              </div>
            </div>

            {/* Order Items */}
            <div className="space-y-3 mb-4">
              {order.items.map((item, index) => (
                <div key={index} className="flex gap-4">
                  <img
                    src={item.image || "/placeholder.png"}
                    alt={item.name}
                    className="w-16 h-16 object-cover rounded"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{item.name}</p>
                    <p className="text-sm text-gray-600">
                      Qty: {item.quantity} × ₹{item.price}
                    </p>
                  </div>
                  <p className="font-semibold text-gray-900">
                    ₹{(item.quantity * item.price).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>

            {/* Shipping Address */}
            <div className="bg-gray-50 rounded p-4 mb-4">
              <h4 className="font-semibold text-gray-900 mb-2">Shipping Address</h4>
              <p className="text-sm text-gray-700">
                {order.shippingAddress.fullName}
              </p>
              <p className="text-sm text-gray-700">
                {order.shippingAddress.phone}
              </p>
              <p className="text-sm text-gray-700">
                {order.shippingAddress.addressLine1}
                {order.shippingAddress.addressLine2 && `, ${order.shippingAddress.addressLine2}`}
              </p>
              <p className="text-sm text-gray-700">
                {order.shippingAddress.city}, {order.shippingAddress.state} - {order.shippingAddress.pincode}
              </p>
            </div>

            {/* Payment Info */}
            <div className="flex justify-between items-center text-sm">
              <div>
                <span className="text-gray-600">Payment Method: </span>
                <span className="font-semibold">{order.paymentMethod}</span>
                <span className="mx-2">•</span>
                <span className="text-gray-600">Status: </span>
                <span className={`font-semibold ${
                  order.paymentStatus === "Completed" ? "text-green-600" : "text-yellow-600"
                }`}>
                  {order.paymentStatus}
                </span>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={() => navigate(`/orders/${order._id}`)}
                  className="text-purple-600 hover:text-purple-700 font-medium"
                >
                  View Details
                </button>
                {order.orderStatus !== "Cancelled" && order.orderStatus !== "Delivered" && (
                  <>
                    <span className="text-gray-300">|</span>
                    <button
                      onClick={() => handleCancelOrder(order._id)}
                      className="text-red-600 hover:text-red-700 font-medium"
                    >
                      Cancel Order
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}